import { LitElement, html, css } from 'lit'
import { updateQuantity, updateQuantityMultiple, reRenderShopifySections } from './lib/utils'
import { visuallyHidden } from './lib/styles/mixins'
import { ref, createRef } from 'lit/directives/ref.js'
import { addToCart } from './lib/utils/requests.js'

class HHMiniCart extends LitElement {
  static properties = {
    loading: {
      reflect: true,
    },
    _trapFocus: {
      state: true,
    },
    errorMessage: {
      type: String,
      attribute: 'error-message',
    },
  }

  $header = createRef()

  constructor() {
    super()

    this.handleBodyClick = this.handleBodyClick.bind(this)
    this.closeWithEscape = this.closeWithEscape.bind(this)

    this.addEventListener(
      'transitionend',
      () => {
        this._trapFocus = true
      },
      { once: true }
    )
  }

  connectedCallback() {
    super.connectedCallback()
    document.body.addEventListener('click', this.handleBodyClick)
    document.addEventListener('productAdded', this.refreshShoppingGives.bind(this))

    setTimeout(() => {
      this.calculateHeight()
    }, 5)
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    document.body.removeEventListener('click', this.handleBodyClick)
    document.removeEventListener('productAdded', this.refreshShoppingGives.bind(this))
  }

  calculateHeight() {
    const headerHeight = this.$header?.value?.offsetHeight || 0
    const footerHeight =
      this.querySelector('.js-mini-cart-footer')?.offsetHeight || 0
    const donateHeight =
      this.querySelector('.js-shopping-gives')?.offsetHeight || 0
    const samplesHeight =
      this.querySelector('.js-free-items')?.offsetHeight || 0

    document
      .querySelector(':root')
      .style.setProperty(
        `--hh-mini-cart-body-height`,
        `calc(100dvh - ${headerHeight}px - ${footerHeight}px)`
      )
  }

  handleBodyClick(e) {
    if (
      e.composedPath().some((node) => {
        return node.matches && node.matches('hh-mini-cart')
      })
    ) {
      return true
    }

    if (!e.defaultPrevented && this.isOpen()) {
      this.closeFn()
    }
  }

  isOpen() {
    return !!document.body.classList.contains('mini-cart-is-active')
  }

  closeFn() {
    document
      .querySelector('body')
      .classList.remove('scroll-lock', 'mini-cart-is-active')
    document.querySelector('html').classList.remove('scroll-lock')
    document.querySelector('.js-overlay').classList.remove('active')
  }

  closeWithEscape(e) {
    if (e.code.toUpperCase() === 'ESCAPE') {
      this.closeFn()
    }
  }

  async onChange(event) {
    this.loading = true
    let sections = 'mini-cart,header'
    let sectionsURL = window.location.pathname

    document.activeElement.blur()

    if (!!document.querySelector('.js-product-main')) {
      sections = `${sections},${document.querySelector('.js-product-main').dataset.sectionId}`
      sectionsURL = document.querySelector('.js-product-main').dataset.url
    }

    const response = await updateQuantity(event.detail.key, event.detail.value || 0, sections, sectionsURL)

    if (response.errors) {
      this.loading = false
      this.updateErrorMessage(event.detail.key, response.errors);

      return;
    }

    this.updateErrorMessage(event.detail.key, '');

    reRenderShopifySections(
      response
    )

    await this.checkAvailableFreeGift()

    setTimeout(() => {
      this.refreshShoppingGives()
      this.calculateHeight()
    }, 100)

    this.loading = false
  }

  onReachedMaxQuantity(event) {
    this.updateErrorMessage(event.detail.key, this.errorMessage.replace('{{ max }}', event.detail.value));
  }

  async onRemove(event) {
    event?.detail?.event?.preventDefault()
    await this.onChange(event)
  }

  async addFreeGift(event) {
    event?.detail?.event?.preventDefault()
    this.loading = true
    reRenderShopifySections(
      await addToCart({
        form_type: 'product',
        id: event.detail.key,
        quantity: 1,
        utf8: '✓',
        properties: {
          '_free': true,
          '_is_free_gift': true
        }
      })
    )
    setTimeout(() => {
      this.calculateHeight()
    }, 100)

    this.loading = false
  }

  refreshShoppingGives () {
    if (!this.querySelector('.js-shopping-gives')) return;

    const addOns = window.sgAddOnControllers
    addOns.forEach(addOn => {
      this.querySelector('.js-shopping-gives').innerHTML = addOn.targetElement.innerHTML
    })
  }

  async checkAvailableFreeGift() {
    const disabled = this.querySelector('.js-gift-unavaialble')
    if (disabled) {
      let gift_ids = disabled.getAttribute('data-gift-ids').split(',')
        .filter(e => !!e)

      if(gift_ids.length > 0) {
        const updates = {}
        gift_ids.forEach(index => updates[index] = 0)
        reRenderShopifySections(
          await updateQuantityMultiple(updates)
        )
      }
    }
  }

  updateErrorMessage (line, message) {
    const lineItemError = document.getElementById(`cart-item-error-${line}`)

    if (lineItemError) lineItemError.querySelector('.cart-item__error-text').innerHTML = message;
  }

  render() {
    return html`
      <hh-focus-trap
        active="${this._trapFocus}"
        @keyup="${this.closeWithEscape}"
      >
        <div class="header" ${ref(this.$header)}>
          <slot name="title"></slot>
          <slot name="close" @click="${this.closeFn}"></slot>
        </div>
        <slot
          name="body"
          @changeQuantity="${this.onChange}"
          @reachedMaxQuantity="${this.onReachedMaxQuantity}"
          @removeItem="${this.onRemove}"
          @addFreeGift="${this.addFreeGift}"
        ></slot>
      </hh-focus-trap>
    `
  }

  static styles = [
    visuallyHidden,
    css`
      :host hh-focus-trap {
        display: flex;
        flex-direction: column;
        height: 100%;
      }

      .header {
        border-bottom: 1px solid var(--hh-color-grey-mineral);
        display: flex;
        justify-content: space-between;
        padding: 20px;
      }
    `,
  ]
}

customElements.define('hh-mini-cart', HHMiniCart)
